/**
 * Compliance Service — D.L. 144/2024 data protection + user rights.
 *
 * Provides the client-side entry points for:
 * - Account deletion request (right-to-erasure, 30-day grace period)
 * - Cancel pending deletion
 * - Record consent for a specific consent_type
 * - Data export / portability (right-to-portability, real export — invokes
 *   the export-user-data edge function which aggregates all PII keyed to
 *   the caller's profile_id and returns it as a single JSON envelope)
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'

type ConsentType =
  | 'data_processing'
  | 'marketing_email'
  | 'marketing_push'
  | 'marketing_sms'
  | 'third_party_sharing'
  | 'analytics'
  | 'location_tracking'
  | 'biometric_data'
  | 'cross_border_transfer'

type LegalBasis =
  | 'consent'
  | 'contract'
  | 'legal_obligation'
  | 'vital_interests'
  | 'public_interest'
  | 'legitimate_interest'

interface ConsentInput {
  consentType: ConsentType
  granted: boolean
  purpose: string
  legalBasis: LegalBasis
  consentFormVersion: string
  consentFormUrl?: string
}

export interface DeletionStatus {
  deleteRequestedAt: string | null
  deleteScheduledAt: string | null
  deletionReason: string | null
}

class ComplianceServiceImpl {
  /**
   * Record a consent grant or revocation. Uses the immutable consent_records
   * table so every change is auditable per D.L. 144/2024.
   */
  async recordConsent(userId: string, input: ConsentInput): Promise<void> {
    void userId
    const { error } = await supabase.rpc('record_consent', {
      p_consent_type: input.consentType,
      p_granted: input.granted,
      p_purpose: input.purpose,
      p_legal_basis: input.legalBasis,
      p_consent_form_version: input.consentFormVersion,
      p_consent_form_url: input.consentFormUrl ?? null,
    })
    // 23505 is the previous unique-active race. The RPC is idempotent for the
    // same grant; a concurrent double-tap can still collide once on old
    // isolates before the migration is applied.
    if (error && error.code !== '23505') throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Request account deletion. Enters a 30-day grace period. Dispatches via
   * the `request_account_deletion` Postgres RPC which also logs a consent
   * revocation entry for the audit trail.
   */
  async requestDeletion(reason?: string): Promise<void> {
    const { error } = await supabase.rpc('request_account_deletion', {
      reason: reason ?? undefined,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Cancel a pending account deletion during the grace period.
   */
  async cancelDeletion(): Promise<void> {
    const { error } = await supabase.rpc('cancel_account_deletion')
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  /**
   * Read the current deletion state so the UI can show the grace period
   * countdown + a cancel button.
   */
  /**
   * D.L. 144/2024 right-to-portability fulfilment. Invokes the
   * `export-user-data` edge function which aggregates the caller's
   * PII across every table keyed to their profile_id and returns one
   * JSON envelope. Caller is expected to hand the response off to the
   * platform's share / save sheet (see app/(tabs)/profile/data-export.tsx).
   */
  async requestDataExport(): Promise<Record<string, unknown>> {
    const { data, error } = await supabase.functions.invoke('export-user-data', {
      body: {},
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) {
      throw new ServiceError({
        message: 'Data export returned empty response',
        code: 'DATA_EXPORT_EMPTY',
        statusCode: 500,
      })
    }
    return data as Record<string, unknown>
  }

  async getDeletionStatus(userId: string): Promise<DeletionStatus> {
    // The deletion columns are deliberately outside the public grant — they say
    // whether someone is on their way out and why. Own row, definer RPC.
    void userId
    const { data, error } = await supabase.rpc('current_profile')
    if (error) throw ServiceError.fromSupabaseError(error)
    return {
      deleteRequestedAt: (data?.delete_requested_at as string) ?? null,
      deleteScheduledAt: (data?.delete_scheduled_at as string) ?? null,
      deletionReason: (data?.deletion_reason as string) ?? null,
    }
  }
}

export const complianceService = new ComplianceServiceImpl()
